import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Video, Phone, Bell, ArrowLeft, SquarePen, Users, Paintbrush } from "lucide-react";
import { useNotificationStream } from "@/hooks/useNotificationStream";
import { lastSeenLabel } from "@/hooks/usePresence";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import FuturoChatBrand from "@/components/FuturoChatBrand";
import GroupCallDialog from "@/components/GroupCallDialog";
import PresenceAvatar from "@/components/PresenceAvatar";
import ActiveContactsStrip from "@/components/ActiveContactsStrip";
import MessageBubble from "@/components/chat/MessageBubble";
import Composer from "@/components/chat/Composer";
import NewChatDialog from "@/components/chat/NewChatDialog";
import ForwardDialog from "@/components/chat/ForwardDialog";
import GroupInfoDialog from "@/components/chat/GroupInfoDialog";
import ChatWallpaperDialog, { wallpaperStyle } from "@/components/chat/ChatWallpaperDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chat() {
  const { user } = useAuth();
  const { otherId: routeId } = useParams();
  const navigate = useNavigate();
  const { unread: activityUnread } = useNotificationStream();
  const [conversations, setConversations] = useState([]);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [call, setCall] = useState(null); // {audioOnly}
  const scrollRef = useRef(null);
  const convId = routeId && routeId.startsWith("conv_") ? routeId : null;

  const scrollToBottom = (smooth = true) => { const el = scrollRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" }); };

  const loadConversations = useCallback(async () => {
    try { const r = await axios.get(`${API}/chat/conversations`); setConversations(r.data || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { loadConversations(); const iv = setInterval(loadConversations, 6000); return () => clearInterval(iv); }, [loadConversations]);

  // Legacy links /chat/user_xxx → open/create direct conversation
  useEffect(() => {
    if (routeId && routeId.startsWith("user_")) {
      axios.post(`${API}/chat/conversations/direct`, { user_id: routeId })
        .then(r => navigate(`/chat/${r.data.conversation_id}`, { replace: true }))
        .catch(() => { toast.error("Usuário não encontrado"); navigate("/chat", { replace: true }); });
    }
  }, [routeId, navigate]);

  useEffect(() => {
    if (!convId) { setConv(null); setMessages([]); return; }
    let cancelled = false;
    setMessages([]); setReplyTo(null); setEditingMsg(null); setDraft(null);
    axios.get(`${API}/chat/conversations/${convId}`).then(r => !cancelled && setConv(r.data)).catch(() => { toast.error("Conversa não encontrada"); navigate("/chat"); });
    const fetchMsgs = async (first) => {
      try {
        const r = await axios.get(`${API}/chat/conversations/${convId}/messages`);
        if (cancelled) return;
        setMessages(prev => [...(r.data.messages || []), ...prev.filter(m => m.pending)]);
        setTyping(r.data.typing || []);
        if (first) { setTimeout(() => scrollToBottom(false), 50); loadConversations(); window.dispatchEvent(new CustomEvent("chat:unread-refresh")); }
        else if ((r.data.messages || []).some(m => m.from_user_id !== user?.user_id && !(m.read_by || []).includes(user?.user_id))) { loadConversations(); window.dispatchEvent(new CustomEvent("chat:unread-refresh")); }
      } catch { /* silent */ }
    };
    fetchMsgs(true);
    const iv = setInterval(() => fetchMsgs(false), 2500);
    const civ = setInterval(() => axios.get(`${API}/chat/conversations/${convId}`).then(r => !cancelled && setConv(r.data)).catch(() => {}), 15000);
    return () => { cancelled = true; clearInterval(iv); clearInterval(civ); };
  }, [convId, navigate]);

  const send = async (payload) => {
    const tempId = `tmp_${Date.now()}`;
    const optimistic = { message_id: tempId, from_user_id: user.user_id, from_name: user.name, from_picture: user.picture, created_at: new Date().toISOString(), pending: true, reply_to: replyTo ? { from_name: replyTo.from_name, preview: replyTo.content || replyTo.type } : null, ...payload };
    setMessages(prev => [...prev, optimistic]);
    const rt = replyTo; setReplyTo(null);
    setTimeout(() => scrollToBottom(), 30);
    try {
      const r = await axios.post(`${API}/chat/conversations/${convId}/messages`, { ...payload, reply_to_id: rt?.message_id || null });
      setMessages(prev => prev.map(m => (m.message_id === tempId ? r.data : m)));
      loadConversations();
    } catch (e) {
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      toast.error(e.response?.data?.detail || "Não foi possível enviar. Tente novamente.");
    }
  };

  const del = async (m, scope) => {
    if (scope === "all" && !window.confirm("Apagar esta mensagem para todos?")) return;
    try {
      await axios.delete(`${API}/chat/messages/${m.message_id}`, { params: { scope } });
      setMessages(prev => scope === "me" ? prev.filter(x => x.message_id !== m.message_id) : prev.map(x => x.message_id === m.message_id ? { ...x, deleted_for_all: true, content: "", media_url: null } : x));
      loadConversations();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao apagar"); }
  };

  const react = async (m, emoji) => {
    try {
      const r = await axios.post(`${API}/chat/messages/${m.message_id}/react`, { emoji });
      setMessages(prev => prev.map(x => x.message_id === m.message_id ? { ...x, reactions: r.data.reactions } : x));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reagir"); }
  };

  const saveEdit = async (m, content) => {
    try {
      const r = await axios.patch(`${API}/chat/messages/${m.message_id}`, { content });
      setMessages(prev => prev.map(x => x.message_id === m.message_id ? { ...x, ...r.data } : x));
      setEditingMsg(null);
      toast.success("Mensagem editada");
      loadConversations();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao editar"); }
  };

  const cutToComposer = (m) => {
    if (!m?.content) return;
    navigator.clipboard?.writeText(m.content).catch(() => {});
    setEditingMsg(null);
    setDraft({ text: m.content, key: Date.now() });
    toast.success("Texto copiado para o campo de mensagem");
  };

  const leaveGroup = async () => {
    if (!window.confirm("Sair do grupo?")) return;
    await axios.delete(`${API}/chat/groups/${convId}/members/${user.user_id}`).catch(() => {});
    navigate("/chat"); loadConversations();
  };

  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [wallpaperOpen, setWallpaperOpen] = useState(false);
  const [wallpapers, setWallpapers] = useState({ default: null, by_conversation: {} });
  useEffect(() => { axios.get(`${API}/chat/wallpaper`).then((r) => setWallpapers(r.data)).catch(() => {}); }, []);
  const activeWallpaper = (convId && wallpapers.by_conversation?.[convId]) || wallpapers.default || null;

  const isGroup = conv?.type === "group";
  const othersCount = Math.max(1, (conv?.members?.length || 2) - 1);
  const isAdmin = isGroup && conv?.admins?.includes(user?.user_id);
  const totalUnread = conversations.reduce((a, c) => a + (c.unread || 0), 0);
  const subtitle = typing.length ? `${typing.join(", ")} digitando...` : isGroup ? `${conv.members.length} membros · ${conv.members_info.filter(m => m.online && m.user_id !== user.user_id).length} online` : lastSeenLabel(conv?.members_info?.find(m => m.user_id !== user?.user_id));

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid md:grid-cols-[340px_1fr] gap-4 h-[calc(100dvh-210px)] min-h-[440px]">
        <Card className={`p-2 overflow-y-auto ${convId ? "hidden md:block" : "block"}`} data-testid="threads-list">
          <div className="flex items-center justify-between px-3 py-2">
            <h2 className="text-xl font-bold" data-testid="chat-page-title"><FuturoChatBrand size="lg" /></h2>
            <div className="flex items-center gap-2">
              {totalUnread > 0 && <span data-testid="threads-total-unread" className="text-[10px] bg-red-500 text-white rounded-full px-2 py-0.5 font-semibold">{totalUnread}</span>}
              <Button size="icon" variant="ghost" className="rounded-full" data-testid="new-chat-btn" onClick={() => setNewChatOpen(true)} aria-label="Nova conversa"><SquarePen className="w-5 h-5" /></Button>
            </div>
          </div>
          <ActiveContactsStrip />
          <button data-testid="thread-activity" onClick={() => navigate("/notifications")} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted text-left border-b border-border/60 mb-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-600 text-white flex items-center justify-center shrink-0"><Bell className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold">Atividade</div><div className="text-xs truncate text-muted-foreground">Curtidas, comentários e marcações</div></div>
            {activityUnread > 0 && <span className="text-[10px] bg-red-500 text-white rounded-full min-w-5 h-5 flex items-center justify-center px-1 font-semibold">{activityUnread > 99 ? "99+" : activityUnread}</span>}
          </button>
          {conversations.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm"><MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />Nenhuma conversa ainda
              <Button size="sm" variant="outline" className="mt-3 rounded-full" data-testid="new-chat-empty-btn" onClick={() => setNewChatOpen(true)}>Iniciar conversa</Button></div>
          )}
          {conversations.map(c => (
            <button key={c.conversation_id} data-testid={`thread-${c.conversation_id}`} onClick={() => navigate(`/chat/${c.conversation_id}`)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left ${convId === c.conversation_id ? "bg-muted" : ""}`}>
              {c.type === "group" ? (
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">{c.picture ? <img src={c.picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <Users className="w-5 h-5" />}</div>
              ) : <PresenceAvatar src={c.picture} name={c.name} online={c.online} testid={`thread-avatar-${c.conversation_id}`} />}
              <div className="flex-1 min-w-0">
                <div className={`text-sm truncate ${c.unread ? "font-bold" : "font-semibold"}`}>{c.name}</div>
                <div className={`text-xs truncate ${c.unread ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
                  {c.last_message ? <>{c.last_message.from_user_id === user?.user_id ? "Você: " : (c.type === "group" && c.last_message.from_name ? `${c.last_message.from_name.split(" ")[0]}: ` : "")}{c.last_message.text}</> : "Sem mensagens"}
                </div>
              </div>
              {c.unread > 0 && <span data-testid={`thread-unread-${c.conversation_id}`} className="text-[10px] bg-emerald-500 text-white rounded-full min-w-5 h-5 flex items-center justify-center px-1 font-semibold">{c.unread}</span>}
            </button>
          ))}
        </Card>

        <Card className={`flex-col min-h-0 overflow-hidden ${convId ? "flex" : "hidden md:flex"}`} data-testid="chat-panel">
          {convId && conv ? (
            <>
              <div className="p-3 border-b border-border flex items-center gap-3">
                <Button size="icon" variant="ghost" className="rounded-full md:hidden -ml-1" data-testid="chat-back-btn" onClick={() => navigate("/chat")} aria-label="Voltar"><ArrowLeft className="w-5 h-5" /></Button>
                {isGroup ? <button type="button" data-testid="group-info-btn" onClick={() => setGroupInfoOpen(true)} className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center overflow-hidden">{conv.picture ? <img src={conv.picture} alt="" className="w-full h-full object-cover" /> : <Users className="w-4 h-4" />}</button>
                  : <PresenceAvatar src={conv.picture} name={conv.name} online={conv.online} className="w-9 h-9" testid="chat-header-avatar" />}
                <button type="button" className="flex-1 min-w-0 text-left" data-testid="chat-header-info" onClick={() => isGroup && setGroupInfoOpen(true)}>
                  <div className="font-semibold truncate" data-testid="chat-header-name">{conv.name}</div>
                  <div className={`text-[11px] truncate ${typing.length ? "text-emerald-600 italic" : conv.online ? "text-emerald-600" : "text-muted-foreground"}`} data-testid="chat-header-presence">{subtitle}</div>
                </button>
                {isGroup && <Button size="icon" variant="ghost" className="rounded-full" data-testid="group-add-member" onClick={() => setGroupInfoOpen(true)} aria-label="Dados do grupo"><Users className="w-4 h-4" /></Button>}
                <Button size="icon" variant="ghost" className="rounded-full" data-testid="chat-wallpaper-btn" onClick={() => setWallpaperOpen(true)} aria-label="Papel de parede" title="Papel de parede"><Paintbrush className="w-4 h-4" /></Button>
                <Button size="icon" variant="outline" className="rounded-full" data-testid="chat-voice-call-btn" onClick={() => setCall({ audioOnly: true })} aria-label="Chamada de voz"><Phone className="w-4 h-4" /></Button>
                <Button size="icon" variant="outline" className="rounded-full" data-testid="chat-video-call-btn" onClick={() => setCall({ audioOnly: false })} aria-label="Chamada de vídeo"><Video className="w-4 h-4" /></Button>
              </div>
              <div className={`flex-1 min-h-0 overflow-y-auto p-4 ${activeWallpaper ? "" : "bg-[radial-gradient(ellipse_at_top,hsl(var(--muted))_0%,transparent_60%)]"}`} style={wallpaperStyle(activeWallpaper)} data-testid="chat-messages-area" data-wallpaper={activeWallpaper ? activeWallpaper.type : "default"} ref={scrollRef}>
                <div className="space-y-2" data-testid="chat-messages">
                  {messages.length === 0 && <div className="text-center text-xs text-muted-foreground py-10" data-testid="chat-empty">Diga oi 👋</div>}
                  {messages.map(m => <MessageBubble key={m.message_id} m={m} mine={m.from_user_id === user?.user_id} isGroup={isGroup} othersCount={othersCount} onReply={setReplyTo} onForward={setForwardMsg} onDelete={del} onReact={react} onEdit={(msg) => { setReplyTo(null); setEditingMsg(msg); }} onCut={cutToComposer} me={user?.user_id} />)}
                  {typing.length > 0 && <div className="text-xs text-muted-foreground italic pl-9" data-testid="typing-indicator">{typing.join(", ")} digitando...</div>}
                </div>
              </div>
              <ChatWallpaperDialog open={wallpaperOpen} onOpenChange={setWallpaperOpen} conversationId={convId} conversationName={conv.name} current={activeWallpaper} onSaved={setWallpapers} />
              <Composer conversationId={convId} replyTo={replyTo} onCancelReply={() => setReplyTo(null)} onSend={send} editing={editingMsg} onCancelEdit={() => setEditingMsg(null)} onSaveEdit={saveEdit} draft={draft} />
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
              <MessageSquare className="w-10 h-10 opacity-30" /><span>Selecione uma conversa</span>
              <Button size="sm" variant="outline" className="rounded-full" data-testid="new-chat-side-btn" onClick={() => setNewChatOpen(true)}>Nova conversa</Button>
            </div>
          )}
        </Card>
      </div>

      <NewChatDialog open={newChatOpen} onOpenChange={setNewChatOpen} onOpenConversation={(id) => { navigate(`/chat/${id}`); loadConversations(); }} />
      <ForwardDialog message={forwardMsg} conversations={conversations} onClose={() => setForwardMsg(null)} />
      <GroupInfoDialog conv={isGroup ? conv : null} me={user?.user_id} open={groupInfoOpen && isGroup} onOpenChange={setGroupInfoOpen} onChanged={(c) => { setConv(c); loadConversations(); }} onLeave={() => { setGroupInfoOpen(false); leaveGroup(); }} />
      <GroupCallDialog
        open={!!call && !!conv}
        onOpenChange={(v) => !v && setCall(null)}
        participants={conv ? conv.members.filter(m => m !== user?.user_id) : []}
        audioOnly={!!call?.audioOnly}
        title={`${call?.audioOnly ? "Chamada de voz" : "Chamada de vídeo"} — ${conv?.name || ""}`}
      />
    </div>
  );
}
